import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Card, DatePicker, Drawer, Empty, List, Select, Spin, Table, Tag, Tooltip, message } from "antd";
import { EyeOutlined, UnorderedListOutlined } from "@ant-design/icons";
import moment from "moment";
import { getPreSignedUrl, getSurveyMonthlySummary, getSurveySubmissionById, getSurveySubmissionHistory, getSurveyTemplates } from "./educationApi";
import OrgFacilityFilter from "./OrgFacilityFilter";
import {
  SURVEY_DATE_TIME_FORMAT,
  SURVEY_MONTH_KEY_FORMAT,
  SURVEY_MONTH_LABEL_FORMAT,
  getSurveyClientId,
  getSurveyList,
  isSurveyOk,
  normalizeSubmissions,
  scrollToTopOnTableChange,
  useColumnSearch,
} from "./surveyUtils";

const { Option } = Select;

const labelStyle = { marginBottom: 4, fontSize: 13, color: "#5B6472" };

// Deliberately a plain read-only list rather than a DynamicSurveyForm — this
// view only needs to show what was answered, so it stays lightweight.
const SubmissionHistoryDrawer = ({ visible = false, onClose, loading = false, submissions = [], title = "Submissions" }) => (
  <Drawer title={title} placement="right" width={640} visible={visible} onClose={onClose}>
    <Spin spinning={loading}>
      {submissions.length === 0 && !loading ? (
        <Empty description="No submissions found for this month." image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List
          dataSource={submissions}
          rowKey={(submission) => submission.id}
          renderItem={(submission, index) => (
            <List.Item style={{ padding: 0, border: "none", marginBottom: 16 }}>
              <Card
                style={{ width: "100%" }}
                size="small"
                title={`Submission ${index + 1}`}
                extra={submission.created_date ? <Tag>{moment(submission.created_date).format(SURVEY_DATE_TIME_FORMAT)}</Tag> : null}
              >
                <p style={{ marginBottom: 12, color: "#101828" }}>
                  <strong>Respondent:</strong> {submission.respondent_name || "-"}
                  {submission.respondent_type ? ` (${submission.respondent_type})` : ""}
                </p>

                {Array.isArray(submission.answers) && submission.answers.length > 0 ? (
                  submission.answers.map((answer, answerIndex) => (
                    <div key={answer.question_id || `${submission.id}-${answerIndex}`} style={{ marginBottom: 8 }}>
                      <div style={{ color: "#5B6472" }}>
                        {answerIndex + 1}. {answer.question_text || "Question"}
                      </div>
                      <div style={{ whiteSpace: "pre-wrap", color: "#101828" }}>{answer.answer_display || answer.answer_text || "-"}</div>
                    </div>
                  ))
                ) : (
                  <span style={{ color: "#5B6472" }}>No answers recorded.</span>
                )}
              </Card>
            </List.Item>
          )}
        />
      )}
    </Spin>
  </Drawer>
);

// Ported from the old app's surveyHistory/index.js + SubmissionHistoryDrawer.js —
// monthly aggregate rows per facility/survey, with the PDF and the individual
// submissions behind each row.
// Known gap (same as the sibling tabs): the old screen's per-row Email popover
// action isn't wired up here.
const SurveyHistory = () => {
  const columnSearch = useColumnSearch();

  const [orgId, setOrgId] = useState(undefined);
  const [facilities, setFacilities] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [filters, setFilters] = useState({ facility_id: "", survey_template_id: "", month: "" });
  const [month, setMonth] = useState(null);

  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingRowKey, setViewingRowKey] = useState(null);

  const [drawerState, setDrawerState] = useState({ visible: false, title: "" });
  const [monthSubmissions, setMonthSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  const summaryRequestRef = useRef(0);
  const drawerRequestRef = useRef(0);

  useEffect(() => {
    (async () => {
      const response = await getSurveyTemplates({ client_id: getSurveyClientId() });
      setTemplates(getSurveyList(response));
    })();
  }, []);

  useEffect(() => {
    // only forward the filters that are actually set, so the backend can treat the rest as "all"
    const activeFilters = Object.keys(filters).reduce((params, key) => (filters[key] ? { ...params, [key]: filters[key] } : params), {});
    const requestId = ++summaryRequestRef.current;

    (async () => {
      setLoading(true);
      // NB: survey_monthly_summary currently requires facility_id AND
      // survey_template_id (month defaults to the current one) and answers
      // with a single object — so the table stays empty until both are chosen.
      const response = await getSurveyMonthlySummary({ ...activeFilters, client_id: getSurveyClientId() });
      if (summaryRequestRef.current !== requestId) return;

      const result = isSurveyOk(response) ? response.result.result : null;
      setSummaries(Array.isArray(result) ? result : result ? [result] : []);
      setLoading(false);
    })();
  }, [filters]);

  const handleOrgChange = useCallback((value) => {
    setOrgId(value);
    setFilters((previous) => ({ ...previous, facility_id: "" }));
  }, []);

  const handleViewPdf = async (record) => {
    setViewingRowKey(record.row_key);
    const { error, result } = await getPreSignedUrl(record.latest_pdf_link);
    setViewingRowKey(null);
    if (!error && result?.result) window.open(result.result, "_blank");
    else message.error("Could not open the report.");
  };

  // Every individual submission behind one monthly aggregate row.
  const handleViewSubmissions = async (record) => {
    const requestId = ++drawerRequestRef.current;

    setDrawerState({ visible: true, title: `${record.template_name} - ${record.month_label}` });
    setMonthSubmissions([]);
    setLoadingSubmissions(true);

    const response = await getSurveySubmissionHistory({
      facility_id: record.facility_id,
      survey_template_id: record.survey_template_id,
      month: record.submission_month,
      client_id: getSurveyClientId(),
    });
    if (drawerRequestRef.current !== requestId) return;

    // the history list has no answers: load each submission's answers too
    const list = isSurveyOk(response) ? normalizeSubmissions(response.result.result) : [];
    const withAnswers = await Promise.all(
      list.map(async (submission) => {
        if (submission.answers && submission.answers.length) return submission;
        const detail = await getSurveySubmissionById(submission.id);
        const answers = isSurveyOk(detail) && Array.isArray(detail.result.result?.answers) ? detail.result.result.answers : [];
        return { ...submission, answers };
      })
    );
    if (drawerRequestRef.current !== requestId) return;

    setMonthSubmissions(withAnswers);
    setLoadingSubmissions(false);
  };

  const closeDrawer = () => {
    drawerRequestRef.current += 1;
    setDrawerState({ visible: false, title: "" });
    setLoadingSubmissions(false);
  };

  const columns = [
    {
      title: "Facility Name",
      dataIndex: "facility_name",
      key: "facility_name",
      width: "22%",
      ...columnSearch("facility_name", "Facility name"),
    },
    {
      title: "Survey",
      dataIndex: "template_name",
      key: "template_name",
      width: "24%",
      ...columnSearch("template_name", "Survey name"),
    },
    {
      title: "Month",
      dataIndex: "month_label",
      key: "month_label",
      width: "13%",
      align: "center",
    },
    {
      title: "Submissions",
      dataIndex: "submission_count",
      key: "submission_count",
      width: "11%",
      align: "center",
    },
    {
      title: "Last Generated",
      dataIndex: "last_generated_date",
      key: "last_generated_date",
      width: "15%",
      align: "center",
      render: (value) => (value ? moment(value).format(SURVEY_DATE_TIME_FORMAT) : "-"),
    },
    {
      title: "Action",
      key: "action",
      width: "15%",
      align: "center",
      render: (_, record) => (
        <>
          <Tooltip title="View PDF">
            <Button
              size="small"
              icon={<EyeOutlined />}
              aria-label="View PDF"
              disabled={!record.latest_pdf_link}
              loading={viewingRowKey === record.row_key}
              onClick={() => handleViewPdf(record)}
              style={{ marginRight: 8 }}
            />
          </Tooltip>
          <Tooltip title="View Submissions">
            <Button size="small" icon={<UnorderedListOutlined />} aria-label="View Submissions" onClick={() => handleViewSubmissions(record)} />
          </Tooltip>
        </>
      ),
    },
  ];

  const findFacilityName = (facilityId) => {
    const facility = facilities.find((item) => `${item.facility_id ?? item.id}` === `${facilityId}`);
    return facility ? facility.facility_name ?? facility.name : "";
  };

  const findTemplateName = (templateId) => {
    const template = templates.find((item) => `${item.id}` === `${templateId}`);
    return template ? template.name : "";
  };

  const dataSource = summaries.map((summary, index) => {
    const surveyTemplateId = summary.survey_template_id !== undefined ? summary.survey_template_id : summary.template_id;
    // the summary endpoint returns ids only — fall back to the names we already have loaded
    const facilityName = summary.facility_name || findFacilityName(summary.facility_id) || "-";
    const templateName = summary.template_name || summary.name || findTemplateName(surveyTemplateId) || "-";
    const submissionMonth = summary.submission_month || "";
    const monthLabel = submissionMonth ? moment(submissionMonth, SURVEY_MONTH_KEY_FORMAT).format(SURVEY_MONTH_LABEL_FORMAT) : "-";

    return {
      ...summary,
      row_key: summary.id || `${summary.facility_id}-${surveyTemplateId}-${submissionMonth}-${index}`,
      survey_template_id: surveyTemplateId,
      facility_name: facilityName,
      template_name: templateName,
      submission_month: submissionMonth,
      month_label: monthLabel,
      submission_count: Number(summary.submission_count || 0),
      latest_pdf_link: summary.latest_pdf_link || "",
    };
  });

  const filtersIncomplete = !filters.facility_id || !filters.survey_template_id;

  return (
    <div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
        <OrgFacilityFilter
          orgId={orgId}
          onOrgChange={handleOrgChange}
          facilityId={filters.facility_id || undefined}
          onFacilityChange={(value) => setFilters((previous) => ({ ...previous, facility_id: value || "" }))}
          onFacilitiesLoaded={setFacilities}
        />
        <div>
          <div style={labelStyle}>Survey</div>
          <Select
            showSearch
            optionFilterProp="children"
            placeholder="Select survey"
            style={{ width: 240 }}
            allowClear
            value={filters.survey_template_id || undefined}
            onChange={(value) => setFilters((previous) => ({ ...previous, survey_template_id: value || "" }))}
          >
            {templates.map((template) => (
              <Option key={template.id} value={template.id}>
                {template.name}
              </Option>
            ))}
          </Select>
        </div>
        <div>
          <div style={labelStyle}>Month</div>
          <DatePicker
            picker="month"
            format={SURVEY_MONTH_LABEL_FORMAT}
            value={month}
            onChange={(value) => {
              setMonth(value);
              setFilters((previous) => ({ ...previous, month: value ? value.format(SURVEY_MONTH_KEY_FORMAT) : "" }));
            }}
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="row_key"
        loading={loading}
        locale={{ emptyText: filtersIncomplete ? "Select a facility and survey to view its monthly history." : "No survey submissions recorded yet." }}
        onChange={scrollToTopOnTableChange}
      />

      <SubmissionHistoryDrawer visible={drawerState.visible} title={drawerState.title} loading={loadingSubmissions} submissions={monthSubmissions} onClose={closeDrawer} />
    </div>
  );
};

export default SurveyHistory;
